// End-of-comp bonus awards computation.

const REAL_SPECIES = ["Bass", "Yellowbelly", "Cod", "Catfish"];

export function computeBonusAwards(catches, members) {
  const awards = [];
  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));

  const sorted = [...catches].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at),
  );

  // --- First Cast ---
  if (sorted[0]) {
    const m = memberById[sorted[0].catch_owner_id];
    if (m) {
      awards.push({
        key: "first-cast",
        icon: "🎣",
        title: "First Cast",
        member: m,
        stat: `${sorted[0].length_cm.toFixed(0)}cm ${sorted[0].species || "fish"}`,
        copy: `${m.name} got us started with a ${sorted[0].length_cm.toFixed(0)}cm ${(sorted[0].species || "fish").toLowerCase()}.`,
      });
    }
  }

  // --- Species Slayer — anyone who caught all four real species ---
  for (const m of members) {
    const caughtSet = new Set(
      catches
        .filter((c) => c.catch_owner_id === m.id)
        .map((c) => c.species),
    );
    if (REAL_SPECIES.every((s) => caughtSet.has(s))) {
      awards.push({
        key: `species-slayer-${m.id}`,
        icon: "🎯",
        title: "Species Slayer",
        member: m,
        stat: "One of each species",
        copy: `${m.name} caught one of each. Legend.`,
      });
    }
  }

  // --- Late Bloomer — angler whose first catch was on the final day of the comp ---
  if (sorted.length > 1) {
    const lastDate = new Date(sorted[sorted.length - 1].created_at);
    lastDate.setHours(0, 0, 0, 0);
    const firstByAngler = {};
    for (const c of sorted) {
      if (!firstByAngler[c.catch_owner_id]) firstByAngler[c.catch_owner_id] = c;
    }
    for (const [uid, c] of Object.entries(firstByAngler)) {
      const d = new Date(c.created_at);
      d.setHours(0, 0, 0, 0);
      const anyoneEarlier = sorted.some(
        (x) => x.catch_owner_id !== uid && new Date(x.created_at) < new Date(c.created_at),
      );
      if (d.getTime() === lastDate.getTime() && anyoneEarlier) {
        const m = memberById[uid];
        if (m) {
          awards.push({
            key: `late-bloomer-${m.id}`,
            icon: "🌅",
            title: "Late Bloomer",
            member: m,
            stat: "First catch on the final day",
            copy: `${m.name} left it late. Style points.`,
          });
          break; // one is enough
        }
      }
    }
  }

  // --- Consistency King/Queen — smallest range, min 3 catches ---
  let best = null;
  for (const m of members) {
    const mine = catches.filter((c) => c.catch_owner_id === m.id);
    if (mine.length < 3) continue;
    const lens = mine.map((c) => c.length_cm);
    const range = Math.max(...lens) - Math.min(...lens);
    if (best == null || range < best.range) {
      const avg = lens.reduce((a, b) => a + b, 0) / lens.length;
      best = { member: m, range, avg, count: mine.length };
    }
  }
  if (best) {
    awards.push({
      key: `consistency-${best.member.id}`,
      icon: "📏",
      title: "Consistency Award",
      member: best.member,
      stat: `${best.range.toFixed(0)}cm spread over ${best.count} catches`,
      copy: `${best.member.name} catches nothing but ${best.avg.toFixed(0)}cm fish, apparently.`,
    });
  }

  // --- Wooden Spoon — angler with fewest catches (excluding zero; must be unambiguous) ---
  const withAny = members
    .map((m) => ({
      m,
      count: catches.filter((c) => c.catch_owner_id === m.id).length,
    }))
    .filter((x) => x.count > 0);
  if (withAny.length > 1) {
    withAny.sort((a, b) => a.count - b.count);
    if (withAny[1].count > withAny[0].count) {
      const bottom = withAny[0];
      awards.push({
        key: "wooden-spoon",
        icon: "🥄",
        title: "Wooden Spoon",
        member: bottom.m,
        stat: `${bottom.count} catch${bottom.count === 1 ? "" : "es"}`,
        copy: `Better luck next year, ${bottom.m.name}.`,
      });
    }
  }

  return awards;
}
